import { FC, useCallback, useEffect, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import EmptyState from '@/components/seop/EmptyState';
import { useToast } from '@/contexts/ToastContext';
import { seopService } from '@/services/seop.service';
import type { SeopMonth } from '@/types/seop';
import { fmtUnits, seopNomeDoMes } from '@/utils/seopFormat';
import {
  anoMes,
  anosDisponiveis,
  EVENTO_MESES_ALTERADOS,
  mensagemDoMes,
  MESES_DO_ANO,
  mesSeguinte,
} from '@/utils/seopMeses';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const CAMPO = 'rounded-lg border border-theme-border bg-surface-secondary px-2 py-1.5 text-sm text-content-primary';
const BOTAO =
  'rounded-lg border border-theme-border px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50';

/** Mês + ano em dois seletores (o `<input type="month">` não existe no Firefox nem no Safari). */
const SeletorDeMes: FC<{ rotulo: string; valor: string; anos: number[]; onChange: (v: string) => void }> = ({
  rotulo,
  valor,
  anos,
  onChange,
}) => {
  const [ano, mes] = valor.split('-');
  return (
    <span className="inline-flex items-center gap-1">
      <select
        aria-label={`${rotulo}: mês`}
        value={mes}
        onChange={(e) => onChange(`${ano}-${e.target.value}`)}
        className={CAMPO}
      >
        {MESES_DO_ANO.map((nome, i) => (
          <option key={nome} value={String(i + 1).padStart(2, '0')}>
            {nome}
          </option>
        ))}
      </select>
      <select
        aria-label={`${rotulo}: ano`}
        value={ano}
        onChange={(e) => onChange(`${e.target.value}-${mes}`)}
        className={CAMPO}
      >
        {anos.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </span>
  );
};

const Demanda: FC<{ mes: SeopMonth }> = ({ mes }) => {
  if (!mes.demand_total) return <span className="text-content-muted">nada lançado</span>;
  return (
    <>
      {fmtUnits(mes.demand_total)} un
      <span className="ml-2 text-xs text-content-muted">em {fmtUnits(mes.demand_items ?? 0)} produtos</span>
    </>
  );
};

const TabelaDeMeses: FC<{ meses: SeopMonth[]; onTrocar: (m: SeopMonth) => void }> = ({ meses, onTrocar }) => (
  <div className="overflow-x-auto">
    <table className="w-full" data-testid="seop-meses-tabela">
      <thead>
        <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
          <th className="pb-2 pr-3 text-left">Mês</th>
          <th className="pb-2 pr-3 text-left">Produção de</th>
          <th className="pb-2 pr-3 text-left">Demanda lançada</th>
          <th className="pb-2 pr-3 text-left">Ações</th>
        </tr>
      </thead>
      <tbody>
        {meses.map((m) => (
          <tr key={m.id} className="border-b border-theme-border/50" data-testid={`seop-mes-${m.id}`}>
            <td className={`${CELL} font-semibold`}>{m.month_name || seopNomeDoMes(m)}</td>
            <td className={`${CELL} text-content-secondary`}>{m.range ?? '—'}</td>
            <td className={CELL}>
              <Demanda mes={m} />
            </td>
            <td className={CELL}>
              <button type="button" data-testid={`seop-trocar-mes-${m.id}`} onClick={() => onTrocar(m)} className={BOTAO}>
                Corrigir mês…
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Os meses do plano, com nome, janela de produção e quanto de demanda cada um tem.
 *
 * Responde a pergunta que a tela não respondia — "em que mês eu lancei a demanda?" — e dá
 * os dois consertos que faltavam: **adicionar** um mês em qualquer ponto do horizonte (a
 * aba Capacidade só acrescenta o mês seguinte ao último) e **corrigir** o mês de um plano
 * lançado no lugar errado. Corrigir não redigita nada: o que foi lançado e os cenários
 * salvos acompanham o mês.
 */
const MesesDoPlano: FC = () => {
  const { showToast } = useToast();
  const [meses, setMeses] = useState<SeopMonth[] | null>(null);
  const [novo, setNovo] = useState('');
  const [corrigindo, setCorrigindo] = useState<SeopMonth | null>(null);
  const [destino, setDestino] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const lista = [...(await seopService.getMonths())].sort((a, b) => a.date.localeCompare(b.date));
      setMeses(lista);
      const ultimo = lista[lista.length - 1];
      setNovo(ultimo ? mesSeguinte(anoMes(ultimo.date)) : `${new Date().getFullYear()}-01`);
    } catch {
      setMeses([]);
      showToast('Não consegui carregar os meses do plano.', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aplicar = async (acao: () => Promise<SeopMonth>, sucesso: (m: SeopMonth) => string, falha: string) => {
    setOcupado(true);
    try {
      const mes = await acao();
      showToast(sucesso(mes), 'success');
      setCorrigindo(null);
      await carregar();
      window.dispatchEvent(new Event(EVENTO_MESES_ALTERADOS));
    } catch (e) {
      showToast(mensagemDoMes(e) || falha, 'error');
    } finally {
      setOcupado(false);
    }
  };

  const adicionar = () =>
    aplicar(
      () => seopService.addMonth(novo),
      (m) => `${seopNomeDoMes(m)} adicionado, com a capacidade e o mix do mês vizinho.`,
      'Erro ao adicionar o mês.',
    );

  const corrigir = () => {
    if (!corrigindo) return;
    if (destino === anoMes(corrigindo.date)) {
      showToast('Escolha um mês diferente do atual.', 'error');
      return;
    }
    aplicar(
      () => seopService.redateMonth(corrigindo.id, destino),
      (m) => `O plano de ${seopNomeDoMes(corrigindo)} agora é de ${seopNomeDoMes(m)}.`,
      'Erro ao corrigir o mês.',
    );
  };

  if (meses === null) return <p className="text-sm text-content-muted">Carregando os meses…</p>;

  const anos = anosDisponiveis(
    meses.map((m) => m.date),
    new Date().getFullYear(),
  );

  return (
    <div data-testid="seop-meses-do-plano">
      <p className="mb-3 text-sm text-content-secondary">
        O mês do plano é o mês de produção: vai do dia 16 do mês anterior ao dia 15. A demanda lançada num mês aparece
        na Visão geral e no Forecast e vendas nesse mesmo mês.
      </p>

      {meses.length === 0 ? (
        <EmptyState icon="calendar_month" title="Nenhum mês no plano" hint="Adicione o primeiro mês abaixo." />
      ) : (
        <TabelaDeMeses
          meses={meses}
          onTrocar={(m) => {
            setCorrigindo(m);
            setDestino(anoMes(m.date));
          }}
        />
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-content-secondary">Adicionar mês</span>
        <SeletorDeMes rotulo="Mês a adicionar" valor={novo} anos={anos} onChange={setNovo} />
        <button
          type="button"
          data-testid="seop-adicionar-mes"
          onClick={adicionar}
          disabled={ocupado}
          className="rounded-lg bg-accent px-4 py-1.5 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
        >
          Adicionar
        </button>
        <span className="text-xs text-content-muted">
          O mês novo herda a capacidade e o mix do mês vizinho; a demanda começa vazia.
        </span>
      </div>

      <ConfirmModal
        open={corrigindo !== null}
        title={`Corrigir o mês de ${corrigindo ? corrigindo.month_name || seopNomeDoMes(corrigindo) : ''}?`}
        confirmLabel="Corrigir mês"
        tone="accent"
        isBusy={ocupado}
        onConfirm={corrigir}
        onCancel={() => setCorrigindo(null)}
      >
        <p>Use quando o plano foi lançado no mês errado. Nada é redigitado:</p>
        <p>
          <strong>tudo o que está neste mês</strong> (demanda, capacidade, mix e alocações) <strong>e os cenários salvos
          dele</strong> passam a valer para o mês escolhido. A Visão geral e o Forecast acompanham na próxima atualização.
        </p>
        <label className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          Este plano é de
          <SeletorDeMes rotulo="Mês correto" valor={destino} anos={anos} onChange={setDestino} />
        </label>
      </ConfirmModal>
    </div>
  );
};

export default MesesDoPlano;
